import { debounce } from '@mui/material';
import {
 GridColDef,
 GridColumnOrderChangeParams,
} from '@mui/x-data-grid-premium';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { setConfigStorageItem } from '../../../../hooks/useConfigStorage';
import { useCurrentStructure } from '../../../../hooks/useCurrentStructure';
import {
 changeCurrentPreset,
 ColPresetType,
 PageNameType,
} from '../../../../redux/clientConfiguration/clientConfigurationSlice';
import { useAppDispatch } from '../../../../redux/hooks';
import { RootState } from '../../../../redux/store';
import type { StructureType } from '../../../../types/types';
import { COLUMN_RESIZE_DEBOUNCE_MS, DEFAULT_PRESET_NAME } from '../constants';
import { renderColWithStructure } from '../renderCol';

interface ColumnResizeParams {
 colDef: { field: string };
 width: number;
}

interface PresetColumn {
 field: string;
 width?: number;
}

// Build a default columns preset straight from the structure map, so a page
// with no saved/server preset (e.g. IoT devices) still renders columns just
// like Results does. Viewable fields only, ordered by Priority.
const buildDefaultPreset = (structure: StructureType[]): ColPresetType => ({
 name: DEFAULT_PRESET_NAME,
 columns: [...structure]
  .filter((s) => s.IsViewable)
  .sort((a, b) => (a.Priority ?? 0) - (b.Priority ?? 0))
  .map((s) => ({ field: s.Name })),
});

const useColumnPresets = (pageName: string) => {
 const dispatch = useAppDispatch();
 const structure = useCurrentStructure();

 const currentColumnsPreset = useSelector(
  (state: RootState) =>
   state.clientConfiguration.currentColumnsPreset?.[pageName] || null,
 );
 const presetsColumns = useSelector(
  (state: RootState) => state.clientConfiguration.presetsColumns,
 );
 const tempColumnsPreset = useSelector(
  (state: RootState) =>
   state.clientConfiguration.tempColumnsPreset?.[pageName] || null,
 );
 const activePreset = tempColumnsPreset ?? currentColumnsPreset;

 const [columnsState, setColumnsState] = useState<GridColDef[]>([]);
 const activePresetRef = useRef(activePreset);
 activePresetRef.current = activePreset;

 useEffect(() => {
  const firstPreset = presetsColumns?.[pageName]?.[0];
  if (!firstPreset || currentColumnsPreset) return;
  dispatch(
   changeCurrentPreset({
    page: pageName as PageNameType,
    type: 'columns',
    preset: firstPreset,
    temp: false,
   }),
  );
  setConfigStorageItem({
   page: pageName as PageNameType,
   key: 'columns',
   value: firstPreset,
  });
 }, [presetsColumns, currentColumnsPreset, dispatch, pageName]);

 // Fallback: no saved preset and no server preset for this page -> derive one
 // from the structure map so the grid still shows columns.
 useEffect(() => {
  if (currentColumnsPreset || presetsColumns?.[pageName]?.length) return;
  if (!structure?.length) return;
  const defaultPreset = buildDefaultPreset(structure);
  if (!defaultPreset.columns.length) return;
  dispatch(
   changeCurrentPreset({
    page: pageName as PageNameType,
    type: 'columns',
    preset: defaultPreset,
    temp: false,
   }),
  );
  setConfigStorageItem({
   page: pageName as PageNameType,
   key: 'columns',
   value: defaultPreset,
  });
 }, [structure, presetsColumns, currentColumnsPreset, dispatch, pageName]);

 useEffect(() => {
  if (!structure?.length || !currentColumnsPreset) return;
  const colsObj = renderColWithStructure(
   currentColumnsPreset.columns,
   structure,
  );
  setColumnsState(colsObj);
 }, [currentColumnsPreset, structure]);

 const savePresetChange = useCallback(
  (
   newPreset: Partial<ColPresetType> & {
    name: string;
    columns: PresetColumn[];
   },
  ) => {
   setConfigStorageItem({
    page: pageName as PageNameType,
    key: 'columns',
    value: newPreset,
   });
   dispatch(
    changeCurrentPreset({
     page: pageName as PageNameType,
     type: 'columns',
     preset: newPreset,
     temp: true,
    }),
   );
  },
  [pageName, dispatch],
 );

 const handleColumnResize = useMemo(
  () =>
   debounce((params: ColumnResizeParams) => {
    setColumnsState((prevState) =>
     prevState.map((col) =>
      col.field === params.colDef.field ? { ...col, width: params.width } : col,
     ),
    );

    const preset = activePresetRef.current;
    savePresetChange({
     ...preset,
     name: preset?.name || DEFAULT_PRESET_NAME,
     columns: preset?.columns
      ? preset.columns.map((col: PresetColumn) =>
         col.field === params.colDef.field
          ? { ...col, width: Math.round(params.width) }
          : col,
        )
      : [],
    });
   }, COLUMN_RESIZE_DEBOUNCE_MS),
  [savePresetChange],
 );

 useEffect(() => () => handleColumnResize.clear(), [handleColumnResize]);

 const handleColumnOrderChange = useCallback(
  (params: GridColumnOrderChangeParams) => {
   setColumnsState((prevColumns) => {
    const { column } = params;
    const matchIndex = prevColumns.findIndex(
     (col) => col.field === column.field,
    );
    if (matchIndex === -1) return prevColumns;

    const updatedColumns = [...prevColumns];
    updatedColumns.splice(params.oldIndex - 1, 1);
    updatedColumns.splice(params.targetIndex - 1, 0, column);

   const preset = activePresetRef.current;
   savePresetChange({
    ...preset,
    name: preset?.name || DEFAULT_PRESET_NAME,
    columns: updatedColumns.map((col) => ({
     field: col.field,
     width: typeof col.width === 'number' ? Math.round(col.width) : col.width,
    })),
   });

    return updatedColumns;
   });
  },
  [savePresetChange],
 );

 return {
  columnsState,
  currentColumnsPreset,
  presetsColumns,
  structure,
  handleColumnResize,
  handleColumnOrderChange,
 };
};

export default useColumnPresets;

